import "leaflet-path-drag";

import type { LeafletMouseEvent, Map } from "leaflet";
import {
    type ReactNode,
    use,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { FeatureGroup, Rectangle } from "react-leaflet";
import { useSelector } from "react-redux";

import { type MovePoint, useDraggable } from "@/hooks";
import LightModel from "@/models/light";

import { FixtureSelectionContext } from "../fixture-selection-provider";

interface Aperture {
    color: string;
    opacity: number;
}

interface LightProps {
    /**
     * The Light model instance.
     */
    model: LightModel;
    /**
     * Callback when the light is selected.
     */
    onSelect?: (evt: LeafletMouseEvent) => void;
    /**
     * The fill color of the light.
     */
    color?: string;
    /**
     * The opacity of the light layer.
     */
    opacity?: number;
    /**
     * The aperture configuration.
     */
    aperture?: Aperture;
    /**
     * Whether to display the signal aperture.
     */
    showSignalAperture?: boolean;
    /**
     * Callback to toggle legend visibility.
     */
    setLegendVisibility?: (visible: boolean) => void;
    /**
     * The zoom level at which the legend appears.
     */
    legendZoomThreshold?: number;
    /**
     * Callback when the light is moved.
     */
    onMoveEnd: (points: MovePoint[]) => void;
}

const defaultAperture: Aperture = {
    color: "#999",
    opacity: 1,
};

/**
 * Renders a single light on the map with selection, aperture, and legend support.
 *
 * @param {LightProps} props - The properties for the Light component.
 * @returns {ReactNode} The rendered light component.
 */
const Light = ({
    model,
    onSelect,
    color = "#999",
    opacity = 1,
    aperture = defaultAperture,
    showSignalAperture = false,
    setLegendVisibility,
    legendZoomThreshold,
    onMoveEnd,
}: LightProps): ReactNode => {
    const lightRef = useRef(null);
    const apertureRef = useRef(null);

    const isSelected = useSelector(({ map }) =>
        map.general.selectedLights.includes(model.uniqueId),
    );

    const [isMounted, setMounted] = useState(false);
    const selection = use(FixtureSelectionContext);

    const handleClick = (evt: LeafletMouseEvent) => {
        if (evt?.target?._map && evt.target._map.moved) {
            evt.target._map.moved = false;
            return;
        }
        if (onSelect) onSelect(evt);
    };

    useEffect(() => {
        // Register this light's layers in the shared registry
        if (!lightRef.current || !isMounted) return () => {};
        return selection?.add(
            model.uniqueId,
            lightRef.current,
            apertureRef.current,
        );
    }, [model.uniqueId, selection, isMounted]);

    const layerRefs = useMemo(() => [lightRef, apertureRef], []);

    const handleDragStart = useCallback(
        () => setLegendVisibility?.(false),
        [setLegendVisibility],
    );

    const handleDragEnd = useCallback(
        (map: Map) => {
            const shouldShowLegend =
                legendZoomThreshold !== undefined &&
                map.getZoom() >= legendZoomThreshold;

            if (shouldShowLegend) {
                setLegendVisibility?.(true);
            }
        },
        [setLegendVisibility, legendZoomThreshold],
    );

    useDraggable({
        layerRefs,
        uniqueId: model.uniqueId,
        isSelected,
        onMoveEnd,
        onDragStart: handleDragStart,
        onDragEnd: handleDragEnd,
        isMounted: isMounted && !!setLegendVisibility,
    });

    useEffect(() => {
        // Workaround to get the layers to render correctly
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setMounted(true);
    }, []);

    if (!isMounted) return null;

    return (
        <FeatureGroup
            interactive
            eventHandlers={{ click: handleClick }}
            key={`light-group-${model.uniqueId}`}
        >
            <Rectangle
                ref={lightRef}
                draggable
                bounds={model.bounds}
                className="light"
                pathOptions={{
                    // @ts-expect-error - the original implementation uses arbitrary props for drag handling
                    id: model.uniqueId,
                    color: "#000",
                    opacity: 1,
                    weight: 1,
                    fillColor: color,
                    fillOpacity: opacity,
                    fillRule: "nonzero",
                }}
            />
            {showSignalAperture && (
                <Rectangle
                    ref={apertureRef}
                    draggable
                    bounds={model.signalAperture.bounds}
                    pathOptions={{
                        color: "#000",
                        weight: 1,
                        fillColor: color,
                        fillOpacity: opacity,
                        opacity: aperture.opacity,
                        dashArray: [5.5, 6],
                    }}
                />
            )}
        </FeatureGroup>
    );
};

export default Light;
